import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types/entities'
import { getSessionConfig } from '@/lib/session-config'

interface AuthState {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    mfaRequired: boolean
    mfaMethods: string[]
    mfaNeedsSetup: boolean // Flag indicating if MFA secret needs to be set up
    // JWT Security state
    accessToken: string | null
    refreshToken: string | null
    tokenExpiry: number | null
    lastTokenRefresh: number | null
    sessionId: string | null
    // Security monitoring
    securityEvents: any[]
    rateLimitStatus: {
        allowed: boolean
        remaining: number
        resetTime: string | null
    } | null
    // Session timeout warning
    sessionTimeoutWarning: {
        isActive: boolean
        timeRemaining: number
        warningShown: boolean
    }
}

interface AuthActions {
    setUser: (user: User | null) => void
    setLoading: (loading: boolean) => void
    setMfaRequired: (required: boolean, methods?: string[]) => void
    logout: () => void
    forceLogout: () => void
    clearMfa: () => void
    // JWT Security actions
    setTokens: (accessToken: string, refreshToken: string, expiresIn: number) => void
    clearTokens: () => void
    updateTokenExpiry: (expiresIn: number) => void
    setSessionId: (sessionId: string) => void
    // Session timeout handling
    checkSessionTimeout: () => boolean
    checkSessionTimeoutWarning: () => boolean
    showSessionTimeoutWarning: (timeRemaining: number) => void
    hideSessionTimeoutWarning: () => void
    extendSession: () => void
    // Security monitoring actions
    setSecurityEvents: (events: any[]) => void
    setRateLimitStatus: (status: any) => void
    addSecurityEvent: (event: any) => void
    // Initialization
    initializeAuth: () => boolean
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
    persist(
        (set, get) => ({
            // State
            user: null,
            isAuthenticated: false,
            isLoading: false,
            mfaRequired: false,
            mfaMethods: [],
            mfaNeedsSetup: false,
            // JWT Security state
            accessToken: null,
            refreshToken: null,
            tokenExpiry: null,
            lastTokenRefresh: null,
            sessionId: null,
            // Security monitoring
            securityEvents: [],
            rateLimitStatus: null,
            // Session timeout warning
            sessionTimeoutWarning: {
                isActive: false,
                timeRemaining: 0,
                warningShown: false,
            },

            // Actions
            setUser: (user) =>
                set((state) => ({
                    user,
                    // Set isAuthenticated to true if user exists (regardless of MFA state)
                    // The router will handle MFA redirection separately
                    isAuthenticated: !!user,
                    // Don't automatically clear MFA state - let it be set explicitly
                })),

            setLoading: (isLoading) => set({ isLoading }),

            setMfaRequired: (mfaRequired, mfaMethods = [], needsSetup = false) =>
                set({ mfaRequired, mfaMethods, mfaNeedsSetup: needsSetup }),

            logout: () =>
                set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    mfaRequired: false,
                    mfaMethods: [],
                    mfaNeedsSetup: false,
                    accessToken: null,
                    refreshToken: null,
                    tokenExpiry: null,
                    lastTokenRefresh: null,
                    sessionId: null,
                    securityEvents: [],
                    rateLimitStatus: null,
                    sessionTimeoutWarning: {
                        isActive: false,
                        timeRemaining: 0,
                        warningShown: false,
                    },
                }),

            forceLogout: () => {
                // Force clear all state and redirect to login
                set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    mfaRequired: false,
                    mfaMethods: [],
                    mfaNeedsSetup: false,
                    accessToken: null,
                    refreshToken: null,
                    tokenExpiry: null,
                    lastTokenRefresh: null,
                    sessionId: null,
                    securityEvents: [],
                    rateLimitStatus: null,
                    sessionTimeoutWarning: {
                        isActive: false,
                        timeRemaining: 0,
                        warningShown: false,
                    },
                })
                // Force redirect to login page
                window.location.href = '/login'
            },

            clearMfa: () =>
                set({
                    mfaRequired: false,
                    mfaMethods: [],
                    mfaNeedsSetup: false,
                }),

            // JWT Security actions
            setTokens: (accessToken, refreshToken, expiresIn) =>
                set({
                    accessToken,
                    refreshToken,
                    tokenExpiry: Date.now() + (expiresIn * 1000),
                    lastTokenRefresh: Date.now(),
                }),

            clearTokens: () =>
                set({
                    accessToken: null,
                    refreshToken: null,
                    tokenExpiry: null,
                    lastTokenRefresh: null,
                }),

            updateTokenExpiry: (expiresIn) =>
                set({
                    tokenExpiry: Date.now() + (expiresIn * 1000),
                    lastTokenRefresh: Date.now(),
                }),

            // Session timeout handling
            checkSessionTimeout: () => {
                const state = useAuthStore.getState()
                const now = Date.now()
                const config = getSessionConfig()

                // Check if we have isAuthenticated but no valid tokens (inconsistent state)
                if (state.isAuthenticated && (!state.accessToken || !state.refreshToken)) {
                    state.forceLogout()
                    return false
                }

                // Check if token has expired
                if (state.tokenExpiry && now > state.tokenExpiry) {
                    state.forceLogout()
                    return false
                }

                // Check if session has been idle too long
                if (state.lastTokenRefresh && (now - state.lastTokenRefresh) > config.sessionTimeoutMs) {
                    state.forceLogout()
                    return false
                }

                return true
            },

            checkSessionTimeoutWarning: () => {
                const state = useAuthStore.getState()
                const now = Date.now()
                const config = getSessionConfig()

                // Only check if user is authenticated and not already showing warning
                if (!state.isAuthenticated || state.sessionTimeoutWarning.warningShown) {
                    return false
                }

                // Check if we're within warning threshold of session timeout
                if (state.lastTokenRefresh) {
                    const timeUntilTimeout = config.sessionTimeoutMs - (now - state.lastTokenRefresh)

                    if (timeUntilTimeout <= config.warningThresholdMs && timeUntilTimeout > 0) {
                        // Show warning with remaining time
                        state.showSessionTimeoutWarning(Math.ceil(timeUntilTimeout / 1000))
                        return true
                    }
                }

                return false
            },

            showSessionTimeoutWarning: (timeRemaining) =>
                set(() => ({
                    sessionTimeoutWarning: {
                        isActive: true,
                        timeRemaining,
                        warningShown: true,
                    },
                })),

            hideSessionTimeoutWarning: () => {
                const current = get().sessionTimeoutWarning
                // Bail out if already hidden — avoids creating a new object on every
                // user-activity event (scroll, mousemove …) which would trigger
                // unnecessary re-renders across the entire component tree.
                if (!current.isActive && current.timeRemaining === 0 && !current.warningShown) {
                    return
                }
                set(() => ({
                    sessionTimeoutWarning: {
                        isActive: false,
                        timeRemaining: 0,
                        warningShown: false,
                    },
                }))
            },

            extendSession: () => {
                const state = get()
                const config = getSessionConfig()

                // Extend session by updating token expiry
                state.updateTokenExpiry(config.sessionExtensionSeconds)
                // Hide the warning
                state.hideSessionTimeoutWarning()

                // Add security event
                state.addSecurityEvent({
                    type: 'session_extended',
                    timestamp: new Date().toISOString(),
                    userAgent: navigator.userAgent,
                })
            },

            setSessionId: (sessionId) => set({ sessionId }),

            // Security monitoring actions
            setSecurityEvents: (events) => set({ securityEvents: events }),
            setRateLimitStatus: (status) => set({ rateLimitStatus: status }),
            addSecurityEvent: (event) =>
                set((state) => ({
                    securityEvents: [event, ...state.securityEvents].slice(0, 100), // Keep last 100 events
                })),

            // Initialize and validate authentication state
            initializeAuth: () => {
                const state = get()

                // If we have isAuthenticated but no valid tokens, clear the state
                // But don't clear if we're in MFA pending state
                if (state.isAuthenticated && (!state.accessToken || !state.refreshToken) && !state.mfaRequired) {
                    set({
                        user: null,
                        isAuthenticated: false,
                        isLoading: false,
                        mfaRequired: false,
                        mfaMethods: [],
                        accessToken: null,
                        refreshToken: null,
                        tokenExpiry: null,
                        lastTokenRefresh: null,
                        sessionId: null,
                        securityEvents: [],
                        rateLimitStatus: null,
                        sessionTimeoutWarning: {
                            isActive: false,
                            timeRemaining: 0,
                            warningShown: false,
                        },
                    })
                    return false
                }

                // Check session timeout
                return state.checkSessionTimeout()
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated,
                mfaRequired: state.mfaRequired,
                mfaMethods: state.mfaMethods,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                tokenExpiry: state.tokenExpiry,
                lastTokenRefresh: state.lastTokenRefresh,
                sessionId: state.sessionId,
            }),
        }
    )
)

// Selectors
export const useUser = () => useAuthStore((state) => state.user)
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated)
export const useIsLoading = () => useAuthStore((state) => state.isLoading)
export const useMfaRequired = () => useAuthStore((state) => state.mfaRequired)
export const useMfaMethods = () => useAuthStore((state) => state.mfaMethods)
export const useSessionTimeoutWarning = () => useAuthStore((state) => state.sessionTimeoutWarning)

// Helper functions
export const hasRole = (user: User | null, roleName: string): boolean => {
    return user?.roles?.some((role) => role.name === roleName) ?? false
}

export const hasPermission = (user: User | null, resource: string, action: string): boolean => {
    return user?.roles?.some((role) =>
        role.permissions?.some((permission) =>
            permission.resource === resource && permission.action === action
        )
    ) ?? false
}








